import { Component, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ForecastVersionStatus } from '@api-types/enums/forecast-version-status.enum';
import { PmIconComponent } from '@libs/ui';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import {
  DownloadExportComponentData,
  DownloadExportDialogComponent,
} from 'demand-forecasting/components/dialogs/download-export-dialog.component';
import { OptionsHeaderComponent } from 'demand-forecasting/components/forecast-detail/options-header/options-header.component';
import {
  latestCompletedForecastSeries,
  selectForecastSeriesIsPendingOrSaveFilterCombinationPending,
  selectHasForecastSeriesFailedToFetch,
} from 'demand-forecasting/store/forecast-series/forecast-series.selectors';
import { selectForecastVersionByQueryParam } from 'demand-forecasting/store/forecast-version/forecast-version.selectors';
import {
  selectExportId,
  selectFilterCombinationIds,
  selectVersionId,
} from 'demand-forecasting/store/router-state/router.selectors';
import { LucideCircleAlert } from '@lucide/angular';
import { NoVersionIdProvidedComponent } from './no-version-id-provided/no-version-id-provided.component';
import { VersionIsCalculatingComponent } from './version-is-calculating/version-is-calculating.component';
import { VersionIsFetchingComponent } from './version-is-fetching/version-is-fetching.component';
import { VersionIsFinishedResultComponent } from './version-is-finished/version-is-finished-result.component';
import { selectPendingFilterCombinationSeriesIds } from '../store/filter/filter.selectors';

@Component({
  selector: 'pm-forecast-version-state-handler',
  templateUrl: './forecast-version-state-handler.component.html',
  styleUrls: ['./forecast-version-state-handler.component.scss'],
  imports: [
    TranslateModule,
    OptionsHeaderComponent,
    NoVersionIdProvidedComponent,
    VersionIsFinishedResultComponent,
    VersionIsFetchingComponent,
    VersionIsCalculatingComponent,
    PmIconComponent,
  ],
})
export class ForecastVersionStateHandlerComponent implements OnInit {
  ForecastVersionStatus = ForecastVersionStatus;
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);

  protected readonly AlertIcon = LucideCircleAlert;

  protected versionId = this.store.selectSignal(selectVersionId);
  protected isForecastSeriesFetching = this.store.selectSignal(
    selectForecastSeriesIsPendingOrSaveFilterCombinationPending,
  );
  protected hasForecastSeriesFailedToFetch = this.store.selectSignal(
    selectHasForecastSeriesFailedToFetch,
  );

  protected version = this.store.selectSignal(
    selectForecastVersionByQueryParam,
  );

  protected latestForecastSeries = this.store.selectSignal(
    latestCompletedForecastSeries,
  );
  protected exportId = this.store.selectSignal(selectExportId);

  protected selectedQuickFilterIds = this.store.selectSignal(
    selectFilterCombinationIds,
  );

  protected pendingQuickFilterSeriesIds = this.store.selectSignal(
    selectPendingFilterCombinationSeriesIds,
  );

  ngOnInit() {
    const exportId = this.exportId();
    if (exportId) {
      const data: DownloadExportComponentData = {
        exportId,
      };
      this.dialog.open(DownloadExportDialogComponent, {
        data,
      });
    }
  }

  isQuickFilterSeriesPending() {
    const pendingQuickFilterSeriesIds = this.pendingQuickFilterSeriesIds();
    const selectedQuickFilters = this.selectedQuickFilterIds();
    if (
      selectedQuickFilters === undefined ||
      pendingQuickFilterSeriesIds === undefined
    ) {
      return false;
    }
    return (
      selectedQuickFilters.length > 0 && pendingQuickFilterSeriesIds.length > 0
    );
  }
}
